import Vue from 'vue';
import Router from 'vue-router';

import {
  wrapInArray,
  getNumber,
} from '@/utils/common-utils';
import {
  getDefaultAvailability,
  parseAvailabilityStatusQuery,
  PORTAUS_COMPANY_LABELS,
  PORTAUS_COMPANY_OVERRIDE_KEY,
  getPortausCompanyOverride,
} from '@/utils/market-utils';
import {
  get,
} from '@/utils/lodash';

import Store from '@/store';

import Providers from '@/views/provider/providers.vue';
import VueHtml2Canvas from 'vue-html2canvas';
import 'viewerjs/dist/viewer.css'
import Viewer from 'v-viewer'
Vue.use(Viewer)
Vue.use(VueHtml2Canvas);


import userApi from '@/api/user';
import i18n from '@/plugins/vue-i18n';

Vue.use(Router);

// Vanity URL support: map the path-prefix tokens to the values the rest of the
// app already reads from localStorage / i18n. Kept here so the guard stays terse.
const VANITY_LANG_MAP = {
  fr: { code: 'FR', requestValue: 'fr-CA' },
  en: { code: 'EN', requestValue: 'en-US' },
};

// Apply an optional `/:lang/:region` URL prefix to i18n + localStorage so the
// page renders in the right language and market. Purely additive: when the
// prefix is absent (every URL the app currently emits), nothing is touched and
// today's localStorage-driven behavior is preserved. We deliberately do NOT
// emit chanLang/chancountryCode here — on a fresh direct load there are no
// listeners yet, and components initialize from i18n + localStorage.
function applyVanityPrefix(to) {
  const lang = to.params && to.params.lang;
  const region = to.params && to.params.region;
  if (lang && VANITY_LANG_MAP[lang]) {
    localStorage.setItem('setLangCode', VANITY_LANG_MAP[lang].code);
    localStorage.setItem('setLangRequestValue', VANITY_LANG_MAP[lang].requestValue);
    i18n.locale = VANITY_LANG_MAP[lang].requestValue;
  }
  if (region) {
    localStorage.setItem('countryCode', region.toUpperCase());
  }
  applyCompanyOverride(to);
}

// Hidden `?company=<1-4>` switch: previews the provider catalogue of another
// Portaus company (Négoce, PlanVin) before those sites exist. Persisted like
// the region prefix above so it survives in-app navigation — `?company=0`
// (or any invalid value) clears it. Browsing only: checkout and registration
// use getPortausCompanyId(), which ignores this entirely.
function applyCompanyOverride(to) {
  const raw = to.query && to.query.company;
  if (raw != null) {
    const id = parseInt(raw, 10);
    if (PORTAUS_COMPANY_LABELS[id]) {
      localStorage.setItem(PORTAUS_COMPANY_OVERRIDE_KEY, String(id));
    } else {
      localStorage.removeItem(PORTAUS_COMPANY_OVERRIDE_KEY);
    }
  }
  // Mirror into Vuex on every navigation (not just when the param is present)
  // so the banner stays visible while the override is sticky.
  Store.commit('configuration/setPortausCompanyOverride', getPortausCompanyOverride());
}

function lazyLoad(view) {
  return () => import(`@/views/${view}.vue`)
}

const router = new Router({

  scrollBehavior() {
    return {
      x: 0,
      y: 0,
    };
  },

  mode: 'history',
  routes: [
    {
      path: '/:region(on|qc)?',
      name: 'home',
      component: lazyLoad('home/home'),
    },
    {
      path: '/login',
      name: 'Login',
      component: lazyLoad('user/login'),
    },
    {
      path: '/reset-password',
      name: 'Reset-Password',
      component: lazyLoad('user/reset-password'),
    },
    {
      path: '/account',
      name: 'Account',
      component: lazyLoad('user/account'),
      meta: {
        authRequired: true,
      },
    },
    {
      path: '/account/profile',
      name: 'Profile',
      component: lazyLoad('user/profile'),
      meta: {
        authRequired: true,
      },
    },

    {
      path: '/account/sales',
      name: 'SalesHistory',
      component: lazyLoad('user/sales-history'),
      meta: {
        authRequired: true,
      },
    },
    {
      path: '/products',
      name: 'products',
      component: lazyLoad('product/products'),
      props: route => ({
        page: getNumber(get(route.query, 'page'), 1),
        productTypesModel: route.query.inventory != null ? wrapInArray(route.query.inventory) : [],
        productInventoryModel: route.query.inventory != null ? wrapInArray(route.query.inventory) : [],
        availabilityModel: route.query.inventory != null ? wrapInArray(route.query.inventory) : getDefaultAvailability(localStorage.getItem("countryCode")),
        availabilityStatusModel: route.query.show != null ? parseAvailabilityStatusQuery(route.query.show) : ["AVAILABLE", "UPCOMING"],
        attributesModel: route.query.a != null ? wrapInArray(route.query.a) : [],
        countriesModel: route.query.c != null ? wrapInArray(route.query.c) : [],
        regionsModel: route.query.r != null ? wrapInArray(route.query.r) : [],
        minPriceModel: getNumber(get(route.query, 'min')),
        maxPriceModel: getNumber(get(route.query, 'max')),
        setProductTypes: route.query.type != null ? wrapInArray(route.query.type) : [],
      }),
    },
    {
      // Vanity URL support (additive): an OPTIONAL locale/region prefix is
      // accepted in front of the legacy product path. Because :lang and :region
      // are regex-constrained and optional, this single route still matches the
      // legacy `/products/:slug?id=...` (lang/region empty) byte-for-byte, AND
      // the new clean `/fr/qc/products/:slug`. Link-builders that push
      // `{ name: 'product', params: { slug } }` keep producing `/products/:slug`
      // since the optional params are simply omitted at compile time.
      path: '/:lang(fr|en)?/:region(qc|on)?/products/:slug',
      name: 'product',
      component: lazyLoad('product/product'),
      props: route => ({
        slug: get(route.params, 'slug', null),
        // puid: get(route.query, 'puid', null),
        id: get(route.query, 'id', null),
        lang: get(route.params, 'lang', null),
        region: get(route.params, 'region', null),
      }),
    },
    {
      path: '/about-us',
      name: 'about-us',
      component: lazyLoad('about-us/about-us'),
      props: route => ({
        slug: get(route.params, 'slug', null),
        puid: get(route.query, 'puid', null),
      }),
    },
    {
      path: '/privacy-policy',
      name: 'privacy-policy',
      component: lazyLoad('legal/legal-page'),
      props: {
        pageKey: 'privacy',
      },
    },
    {
      path: '/terms-of-use',
      name: 'terms-of-use',
      component: lazyLoad('legal/legal-page'),
      props: {
        pageKey: 'terms',
      },
    },
    {
      path: '/contact-us',
      name: 'contact-us',
      component: lazyLoad('contact/contact-us'),
    },
    {
      path: '/trade-contact',
      name: 'trade-contact',
      component: lazyLoad('contact/trade-contact'),
    },
    {
      path: '/newsletter',
      name: 'newsletter',
      component: lazyLoad('newsletter/newsletter'),
    },
    {
      path: '/providers',
      name: 'providers',
      component: Providers,
      props: route => ({
        type: route.query.type,
        page: getNumber(get(route.query, 'page'), 1),
        attributesModel: route.query.a != null ? wrapInArray(route.query.a) : [],
        countriesModel: route.query.c != null ? wrapInArray(route.query.c) : [],
        regionsModel: route.query.r != null ? wrapInArray(route.query.r) : [],
        textModel: route.query.t,
      }),
    },
    {
      path: '/providers/:slug',
      name: 'provider',
      component: lazyLoad('provider/provider'),
      props: route => ({
        slug: get(route.params, 'slug', null),
      }),
    },
    {
      path: '/blogs',
      name: 'blogs',
      component: lazyLoad('blog/blogs'),
      props: route => ({
        page: getNumber(get(route.query, 'page'), 1),
        blogTypesModel: route.query.type != null ? wrapInArray(route.query.type) : [],
        attributesModel: route.query.a != null ? wrapInArray(route.query.a) : [],
        // authorsModel: route.query.u != null ? wrapInArray(route.query.u) : [],
        // countriesModel: route.query.c != null ? wrapInArray(route.query.c) : [],
        // regionsModel: route.query.r != null ? wrapInArray(route.query.r) : [],
        setDefaultType: route.query.defaultType != null || get(route.params, 'type', null) != null ? wrapInArray(route.query.defaultType) : [],
      }),
    },
    {
      path: '/blogs/:slug',
      name: 'blog',
      component: lazyLoad('blog/blog'),
      props: route => ({
        slug: get(route.params, 'slug', null),
      }),
    },
    {
      path: '/subscriptions',
      name: 'subscription',
      component: lazyLoad('subscription/subscriptions'),
    },
    {
      path: '/global-search',
      name: 'globalSearch',
      component: lazyLoad('global-search/global-search'),
      props: route => ({
        text: route.query.t,
        activeTabType: route.query.activeTabType,
        activeCurrentPage: route.query.activeCurrentPage && parseInt(route.query.activeCurrentPage, 10),
      }),
    },
    {
      path: '/shopping-cart',
      name: 'shoppingCart',
      component: lazyLoad('shopping-cart/shopping-cart'),
    },
    {
      path: '/shopping-cart-empty',
      name: 'shoppingCartEmpty',
      component: lazyLoad('shopping-cart/shopping-cart-empty'),
    },
    {
      path: '/checkout',
      name: 'checkout',
      component: lazyLoad('checkout/checkout'),
    },
    {
      path: '/checkout/confirmation',
      name: 'checkoutConfirmation',
      component: lazyLoad('checkout/checkout-confirmation'),
    },
    {
      path: '/not-found',
      alias: '*',
      name: 'notFound',
      component: lazyLoad('not-found/not-found'),
      props: route => ({
        type: route.query.type,
      }),
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: lazyLoad('user/forgot-password'),
      props: route => ({
        slug: get(route.params, 'slug', null),
        puid: get(route.query, 'puid', null),
      }),
    },
    {
      path: '/faq',
      name: 'faq',
      component: lazyLoad('faq/faq'),
    },
    {
      path: '/services',
      redirect: {
        path: '/faq',
        hash: '#company-services',
      },
    },
    {
      path: '/understand-product',
      redirect: {
        path: '/faq',
        hash: '#company-services',
      },
    },
    {
      path: '/orders-deliveries',
      redirect: {
        path: '/faq',
        hash: '#orders-deliveries',
      },
    },
    {
      path: '/pricing-policy',
      redirect: {
        path: '/faq',
        hash: '#orders-deliveries',
      },
    },
    {
      path: '/membres',
      name: 'membre',
      component: lazyLoad('subscription/membre'),
    },
    {
      path: '/discovery',
      name: 'discovery',
      component: lazyLoad('subscription/discovery')
    },
  ],
});

const getCurrentUser = () => {
  const accessToken = localStorage.getItem('access-token');

  if (accessToken && (get(Store, 'state.user.user.id') == null || localStorage.user != null)) {
    const userId = localStorage.getItem('user') != null ? JSON.parse(localStorage.user).user.id  : null;
    if (userId) {
      return new Promise((resolve, reject) => { // Return a new Promise
        userApi.findById(userId)
          .then((user) => {
            Store.dispatch('user/setUserProfile', user)
              .then(() => { // Wait for the action to complete (if it returns a Promise)
                resolve(); // Resolve the Promise after the store is updated
              })
              .catch((storeError) => {
                reject(storeError);
              });
          })
          .catch((apiError) => {
            reject(apiError);
          });
      });
    } else {
      Store.dispatch('user/logout');
      Store.dispatch('shoppingCart/emptyShoppingCart');
      Store.dispatch('user/createTempUser');
      return Promise.reject('No user ID found');
    }
  }
  return Promise.resolve(); // Resolve immediately if no token or user in store
};

router.beforeEach(async (to, from, next) => {

  // Vanity URL support: honor an optional /:lang/:region path prefix (no-op
  // when absent, i.e. for every URL the app currently generates).
  applyVanityPrefix(to);

  if (to && to.meta && to.meta.authRequired) {

    const accessToken = localStorage.getItem('access-token');

    if (!accessToken) {
      next({
        path: '/login',
        query: {
          redirect: to.fullPath,
        },
      });
      return;
    }

    // Attempt automatic login if token exists
    try {
      await getCurrentUser(to, from, next); // getCurrentUser now returns a Promise
      next(); // If getCurrentUser succeeds, proceed to the route
    } catch (error) {
      // If automatic login fails (e.g., invalid token), redirect to login
      next({
        path: '/login',
        query: {
          redirect: to.fullPath,
        },
      });
    }
  } else {
    if(to.name == 'shoppingCart' || to.name == 'checkout'){
      await getCurrentUser(to, from, next); // getCurrentUser now returns a Promise
    }
    // For routes that don't require authentication, simply proceed
    next();
  }
});

export default router;
